import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Grid } from "@mui/material";
import { connect } from "react-redux";
import { saveIntFlightData, saveTicketSelectedFilters, saveTicketSort } from "../../../store/actions";
import { LocaleContext } from "../../../routing/LangRouter";
import { useTranslation } from "react-i18next";
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";
import { IS_MOBILE } from "../../../constants/breakpoints";

//import custom hooks
import useIntFlightList from "../../../hooks/useIntFlightList";
import useHeaderInfo from "../../../hooks/useHeaderInfo";

//import general components
import MobileHeader from "../../../components/common/mobile-header/mobile-header";
import Loading from "../../../components/common/loading/loading";
import BaseSort from "../../../components/common/base-sort/base-sort";
import FilterSortActions from "../../../components/common/filter-sort-actions/filter-sort-actions";
import ArrowIcon from "../../../components/common/arrow-icon/arrow-icon";
import ExpirationResultModal from "../../../components/common/expiration-result-modal/expiration-result-modal";
import EmptyOrErrorState from "../../../components/common/empty-or-error-state/empty-or-error-state";
import WithoutDestinationModal from "../../../components/common/without-modal/without-destination-modal";
import WithoutDateModal from "../../../components/common/without-modal/without-date-modal";
import WithoutDestinationDateModal from "../../../components/common/without-modal/without-destination-date-modal";
import { BaseButton } from "../../../components/common/base-button/base-button";

//import view components
import IntFlightList from "../../../components/view/flight/international/int-flight-list";
import IntFlightSearchForm from "../../../components/view/flight/international/int-flight-search-form";
import IntFlightSkeleton from "../../../components/view/flight/international/int-flight-skeleton";
import BasePagination from "../../../components/common/base-pagination/base-pagination";
import IntFlightFilter from "../../../components/view/flight/international/int-flight-filter";

//import images and icons
import { Airplane, ArrowLeft2, ArrowRight2, Edit2, InfoCircle, Notification, Warning2 } from "iconsax-react";

function IntFlight({ dispatch, intFlightData, ticketSort, ticketSelectedFilters }) {
    //general variables
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [requestFlag, setRequestFlag] = useState(false);
    const [uid, setUid] = useState(intFlightData?.uid);
    const [isError, setIsError] = useState(false);

    //sessionStorage variables
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary, isAccommodation, isAutomaticReserve } = generalInformation;
    const isTwoWay = intFlightInformation?.returnDate;

    //list of sorting options
    const sortList = [
        { value: "4", label: `${t("cheapest")}`, checked: false },
        { value: "3", label: `${t("expensive")}`, checked: false },
        { value: "1", label: `${t("earliest")}`, checked: true },
        { value: "2", label: `${t("lastest")}`, checked: false },
    ];

    //pagination
    const [page, setPage] = useState(intFlightData?.current_page ?? 1);
    const handlePaginationChange = async (event, value) => {
        setPage(value);
        setUid(intFlightData.uid);
        setRequestFlag(true);
    };

    //get international flight list
    const { getFlightList } = useIntFlightList(
        intFlightInformation,
        uid,
        page,
        ticketSort,
        ticketSelectedFilters,
        setRequestFlag,
        setLoading,
        setIsError,
    );

    //call request flight list on page load and remove session items
    useEffect(() => {
        if (!!intFlightInformation?.departure && !!intFlightInformation?.destination && !!intFlightInformation?.departureDate) {
            getFlightList();
        }
        if (isAutomaticReserve) {
            delete generalInformation.isAutomaticReserve;
            sessionStorage.setItem("generalInformation", JSON.stringify(generalInformation));
        }
    }, []);

    useEffect(() => {
        if (requestFlag) {
            getFlightList();
        }
    }, [requestFlag]);

    //set fligth list
    const [flightList, setFlightList] = useState([]);
    const [requestExpirationDate, setRequestExpirationDate] = useState("");
    useEffect(() => {
        if (!!intFlightData) {
            setFlightList(intFlightData?.results?.list);
            setUid(intFlightData.uid);
            setRequestExpirationDate(intFlightData.exp_at);
        } else {
            setFlightList([]);
            setUid(null);
        }
    }, [intFlightData, ticketSort]);

    // sort initial value
    useEffect(() => {
        dispatch(saveTicketSort("4"));
    }, []);

    // handle sort change
    const handleSelectedRadio = value => {
        dispatch(saveTicketSort(value));
    };

    //skip
    const navigatePage = page => {
        setRoutes([...routes, `/${locale}/itinerary/${page}`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/${page}`]));
        navigate(`/${locale}/itinerary/${page}`);
    };
    const skipLevel = () => {
        if (isAccommodation) {
            navigatePage("hotel");
        } else {
            navigatePage("itinerary-page");
        }
    };

    ////mobile functions
    const goToBack = () => {
        if (isItinerary) {
            navigate(routes[routes.findIndex(route => route === location.pathname) - 1]);
        } else {
            navigate(`/${locale}`);
        }
    };

    //title for the mobile header
    const { headerTitle, headerDetails } = useHeaderInfo("flight", intFlightInformation);

    //handle show edit search form and flight details
    const handleEdittSearch = action => {
        const params = { action };
        setSearchParams(params, { replace: true });
    };

    //handle without destination,without date,and without destination-date modal
    const [openWithoutDate, setOpenWithoutDate] = useState(false);
    const [openWithoutDestination, setOpenWithoutDestination] = useState(false);
    const [openWithoutDestinationDate, setOpenWithoutDestinationDate] = useState(false);
    useEffect(() => {
        const destination = intFlightInformation?.destination;
        const departureDate = intFlightInformation?.departureDate;
        const returnDate = intFlightInformation?.returnDate;
        if (destination !== null && departureDate === null && returnDate === null) setOpenWithoutDate(true);
        else setOpenWithoutDate(false);

        if (destination === null && !!departureDate && !!returnDate) setOpenWithoutDestination(true);
        else setOpenWithoutDestination(false);

        if (destination === null && departureDate === null && returnDate === null) setOpenWithoutDestinationDate(true);
        else setOpenWithoutDestinationDate(false);
    }, []);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    const resetIntFlightList = () => {
        dispatch(saveIntFlightData(null));
    };

    const [openSearchForm, setOpenSearchForm] = useState(false);
    useEffect(() => {
        if (searchParams.get("action") === "edit") setOpenSearchForm(true);
        else setOpenSearchForm(false);
    }, [searchParams]);

    const handleReserve = () => {
        navigate(`/${locale}/flight-automatic-reserve`);
        sessionStorage.setItem(
            "generalInformation",
            JSON.stringify({
                ...generalInformation,
                isAutomaticReserve: true,
            }),
        );
    };

    return (
        <>
            <Grid container item xs={12} justifyContent="center" rowGap={{ xs: 2, md: 5 }} mt={openSearchForm || IS_MOBILE ? 6 : 0} mb={5}>
                {loading && <Loading />}

                <MobileHeader
                    title={headerTitle}
                    details={headerDetails}
                    edit={<Edit2 size="20" color="var(--primary-color)" onClick={() => handleEdittSearch("edit")} />}
                    skip={
                        isItinerary && (
                            <ArrowIcon
                                icons={[ArrowLeft2, ArrowRight2]}
                                isExpanded={locale === "en"}
                                size="24"
                                color="var(--teal-color-18)"
                                onClick={skipLevel}
                            />
                        )
                    }
                    justifyContent="flex-start"
                    isButton={false}
                    handleClick={goToBack}
                />

                <IntFlightSearchForm setRequestFlag={setRequestFlag} openSearchForm={openSearchForm} setUid={setUid} />

                {IS_MOBILE ? (
                    <Grid container item xs={11} mt={2}>
                        <FilterSortActions onFilterClick={() => setIsFilterOpen(true)} onSortClick={() => setIsSortOpen(true)} />
                    </Grid>
                ) : null}

                <Container maxWidth="xl">
                    {loading ? (
                        <IntFlightSkeleton />
                    ) : isError ? (
                        <EmptyOrErrorState
                            title={t("errorMessage")}
                            handleClick={getFlightList}
                            actionLabel={t("retry")}
                            icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                        />
                    ) : !flightList?.length ? (
                        <EmptyOrErrorState
                            title={t("fligthListMessage")}
                            icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                            actionLabel={isItinerary ? t("nextLevel") : ""}
                            handleClick={isItinerary ? skipLevel : null}
                        />
                    ) : (
                        <Grid container item xs={12} rowGap={{ xs: 2, md: 5 }} justifyContent="center">
                            {!!intFlightInformation?.departure ? (
                                <Grid container item xs={12} justifyContent="space-between" alignItems="flex-start">
                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        md={2}
                                        alignItems={{ xs: "flex-end", md: "flex-start" }}
                                        sx={{
                                            position: "sticky !important",
                                            top: "5rem",
                                            zIndex: 10,
                                            maxHeight: { md: "calc(100dvh - 13rem)" },
                                            overflowY: { md: "auto" },
                                            overscrollBehavior: "contain",
                                            scrollbarWidth: "none",
                                            msOverflowStyle: "none",
                                            p: { md: "6px" },
                                            "&::-webkit-scrollbar": {
                                                display: "none",
                                            },
                                            "&::after": {
                                                content: '""',
                                                position: "sticky",
                                                bottom: 0,
                                                display: "block",
                                                width: "100%",
                                                height: "4rem",
                                                flexShrink: 0,
                                                pointerEvents: "none",
                                                background: "linear-gradient(to top, #fff, transparent)",
                                            },
                                        }}
                                    >
                                        <IntFlightFilter
                                            filterList={intFlightData?.results?.filters}
                                            open={isFilterOpen}
                                            setOpen={setIsFilterOpen}
                                            setRequestFlag={setRequestFlag}
                                            setPage={setPage}
                                            setUid={setUid}
                                        />
                                    </Grid>

                                    <Grid container item xs={12} md={9.8} rowGap={2} alignItems="flex-start">
                                        <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                                            <BaseSort
                                                open={isSortOpen}
                                                setOpen={setIsSortOpen}
                                                optionList={sortList}
                                                handleChange={handleSelectedRadio}
                                                defaultValue={ticketSort}
                                                setRequestFlag={setRequestFlag}
                                                setPage={setPage}
                                            />
                                            {!isTwoWay && (
                                                <Grid container item xs={12} md={5} justifyContent="flex-end">
                                                    <Grid container item justifyContent="space-evenly" rowGap={2}>
                                                        <Grid container item xs={12} md={5}>
                                                            <BaseButton
                                                                rightIcon={<Notification />}
                                                                text={t("General.Notify When Available")}
                                                                color="light-gray"
                                                                size="medium"
                                                                radius="radius-10"
                                                                handleClick={() => navigate(`/${locale}/flight-availability`)}
                                                            />
                                                        </Grid>
                                                        <Grid container item xs={12} md={5}>
                                                            <BaseButton
                                                                rightIcon={<Airplane />}
                                                                text={t("General.Reserve When Available")}
                                                                color="light-gray"
                                                                size="medium"
                                                                radius="radius-10"
                                                                handleClick={handleReserve}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            <IntFlightList flightList={flightList} setRequestFlag={setRequestFlag} setUid={setUid} />
                                            {!loading && flightList?.length > 1 && (
                                                <BasePagination
                                                    count={intFlightData?.total_pages}
                                                    page={intFlightData?.current_page ?? 1}
                                                    handleChange={handlePaginationChange}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ) : (
                                <EmptyOrErrorState
                                    title={t("fligthListMessage")}
                                    icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                                />
                            )}
                        </Grid>
                    )}
                </Container>
            </Grid>
            <WithoutDateModal open={openWithoutDate} setOpen={setOpenWithoutDate} setRequestFlag={setRequestFlag} />
            <WithoutDestinationModal open={openWithoutDestination} setOpen={setOpenWithoutDestination} setRequestFlag={setRequestFlag} />
            <WithoutDestinationDateModal open={openWithoutDestinationDate} setOpen={setOpenWithoutDestinationDate} setRequestFlag={setRequestFlag} />
            <ExpirationResultModal
                requestExpirationDate={requestExpirationDate}
                setRequestFlag={setRequestFlag}
                uid={uid}
                setUid={setUid}
                resetList={resetIntFlightList}
                resetFilters={resetFilters}
            />
        </>
    );
}

export default connect(mapStateToProps)(IntFlight);

function mapStateToProps(state) {
    return {
        intFlightData: state.intFlightData,
        ticketSort: state.ticketSort,
        ticketSelectedFilters: state.ticketSelectedFilters,
    };
}
